import { createSlice } from '@reduxjs/toolkit';
import { selectFrom, selectOne, callRPC, updateTable } from '../../services/databaseService';
import { captureOrderPayment, formatCaptureFailure, invokeFunction } from '../../services/edgeFunctionService';
import { supabase } from '../../services/supabase';
import { KanbanColumn, OperationalSummary, Order, OrderState } from '../../@types/order';
// _mock

// redux
import { dispatch } from '../store';
import { getCurrentTenantVenueParams } from '../../utils/tenantVenue';
import { activeOrders, allOrders, paymentStatus } from 'src/_mock';

// ----------------------------------------------------------------------

function objFromArray<Type extends Record<string, any>>(array: Type[], key = 'id') {
  return array.reduce<Record<string, Type>>((accumulator, current) => {
    accumulator[current[key]] = current;
    return accumulator;
  }, {});
}

const initialState: OrderState = {
  isLoading: false,
  error: null,
  orders: [],
  order: null,
  openOrders: [],
  isOpenModal: false,
  isOpenCodeModal: false,
  isOpenNotifyModal: false,
  sortBy: null,
  filterInactiveOrders: false,
  board: {
    cards: {},
    columns: {},
    columnOrder: [],
  },
  statistics: {
    ordersSummary: {
      label: '',
      value: 0,
      count: 0,
      percent: 0,
      total: 0,
      average: 0,
      data: [],
    },
    publicSummary: {
      label: '',
      value: 0,
      count: 0,
      percent: 0,
      total: 0,
      average: 0,
      data: [],
    },
    orderMonthlyTotals: [],
    publicMonthlyTotals: [],
    totalsByProduct: [],
    totalsByStatus: [],
    totalsByType: [],
    totalsByVenue: [],
    totalsByTypePerWeekday: [],
    totalsByPaymentFormat: [],
    entranceFeeSummary: {
      label: '',
      value: 0,
      count: 0,
      percent: 0,
      total: 0,
      average: 0,
      data: [],
    },
    entranceFeeMonthlyTotals: [],
    entranceFeeByWeekday: [],
    entranceFeeByVenue: [],
  },
  operationalSummary: null,
  filters: {
    client: [],
    category: 'All',
    priceRange: [0, 200],
    rating: '',
    status: [],
  }
};

const slice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // STOP LOADING
    stopLoading(state) {
      state.isLoading = false;
    },

    // HAS ERROR
    hasError(state, action) {
      state.error = action.payload;
      state.isLoading = false;
    },

    // GET STATISTICS
    getOrdersStatisticsSuccess(state, action) {
      state.statistics = action.payload;
      state.isLoading = false;
    },

    // GET OPERATIONAL SUMMARY
    getOperationalSummarySuccess(state, action) {
      state.operationalSummary = action.payload;
      state.isLoading = false;
    },

    // GET ORDERS
    getOrdersSuccess(state, action) {
      const orders = Array.isArray(action.payload) ? action.payload : action.payload.orders || [];

      const filterInactiveOrders = state.filterInactiveOrders;

      const ordersData = filterInactiveOrders ? orders : orders.filter((order: { orderstatus: number; }) => order.orderstatus > 0 && order.orderstatus < 4)

      const cards = objFromArray<Order>(ordersData);

      const columnOrder = filterInactiveOrders ? allOrders : activeOrders;

      // Group orders by orderstatus
      const groupedOrders = ordersData.reduce((acc: { [x: string]: any[]; }, order: { orderstatus: any; id: any; }) => {
        const { orderstatus, id } = order;
        if (!acc[orderstatus]) {
            acc[orderstatus] = [];
        }
        acc[orderstatus].push(id);
        return acc;
      }, {});

      const columnsWithData = Object.keys(groupedOrders).reduce((acc, orderstatus) => {
        acc[orderstatus] = {
          id: orderstatus,
          name: orderstatus,
          cardIds: groupedOrders[orderstatus] || []
        };
        return acc;
      }, {} as Record<string, KanbanColumn>);

      const emptyColumns = columnOrder.filter((columnId) => !columnsWithData[columnId]);
      const emptyColumnsData = emptyColumns.reduce((acc, columnId) => {
        acc[columnId] = {
          id: columnId,
          name: columnId,
          cardIds: []
        };
        return acc;
      }, {} as Record<string, KanbanColumn>);

      // Merge empty columns data into columns
      const mergedColumns = { ...columnsWithData, ...emptyColumnsData };

      // Sort columns by id in ascending order
      const sortedColumns = Object.keys(mergedColumns)
        .sort((a, b) => a.localeCompare(b))
        .reduce((acc, key) => {
          acc[key] = mergedColumns[key];
          return acc;
        }, {} as Record<string, KanbanColumn>);

      const columns = sortedColumns;

      state.orders = orders;
      state.board = {
        cards,
        columns,
        columnOrder,
      };
      state.isLoading = false;
    },

    // GET ORDER
    getOrderSuccess(state, action) {
      state.order = action.payload;
      state.isLoading = false;
    },

    // CREATE ORDER
    createOrderSuccess(state, action) {
      state.openOrders = action.payload;
      state.isLoading = false;
    },

    //  SORT & FILTER ORDERS
    sortByOrders(state, action) {
      state.sortBy = action.payload;
    },

    filterInactives(state) {
      const filterInactiveOrders = !state.filterInactiveOrders;
      const orders = state.orders;

      const ordersData = filterInactiveOrders ? orders : orders.filter(order => order.orderstatus > 0 && order.orderstatus < 4)

      const cards = objFromArray<Order>(ordersData);

      const columnOrder = filterInactiveOrders ? allOrders : activeOrders;

      // Group orders by orderstatus
      const groupedOrders = ordersData.reduce((acc: { [x: string]: any[]; }, order: { orderstatus: any; id: any; }) => {
        const { orderstatus, id } = order;
        if (!acc[orderstatus]) {
            acc[orderstatus] = [];
        }
        acc[orderstatus].push(id);
        return acc;
      }, {});

      const columnsWithData = Object.keys(groupedOrders).reduce((acc, orderstatus) => {
        acc[orderstatus] = {
          id: orderstatus,
          name: orderstatus,
          cardIds: groupedOrders[orderstatus] || []
        };
        return acc;
      }, {} as Record<string, KanbanColumn>);

      const emptyColumns = columnOrder.filter((columnId) => !columnsWithData[columnId]);
      const emptyColumnsData = emptyColumns.reduce((acc, columnId) => {
        acc[columnId] = {
          id: columnId,
          name: columnId,
          cardIds: []
        };
        return acc;
      }, {} as Record<string, KanbanColumn>);

      // Merge empty columns data into columns
      const mergedColumns = { ...columnsWithData, ...emptyColumnsData };

      // Sort columns by id in ascending order
      const sortedColumns = Object.keys(mergedColumns)
        .sort((a, b) => a.localeCompare(b))
        .reduce((acc, key) => {
          acc[key] = mergedColumns[key];
          return acc;
        }, {} as Record<string, KanbanColumn>);

      const columns = sortedColumns;

      state.filterInactiveOrders = filterInactiveOrders;
      state.board = {
        cards,
        columns,
        columnOrder,
      };
    },

    filterOrders(state, action) {
      state.filters.client = action.payload.client;
      state.filters.category = action.payload.category;
      state.filters.priceRange = action.payload.priceRange;
      state.filters.rating = action.payload.rating;
    },

    // OPEN MODAL
    openModal(state) {
      state.isOpenModal = true;
    },

    // OPEN CODE MODAL
    openCodeModal(state) {
      state.isOpenCodeModal = true;
    },

    // OPEN NOTIFY MODAL
    openNotifyModal(state) {
      state.isOpenNotifyModal = true;
    },

    // CLOSE MODAL
    closeModal(state) {
      state.isOpenModal = false;
      state.isOpenCodeModal = false;
      state.isOpenNotifyModal = false;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  filterOrders,
  filterInactives,
  openModal,
  closeModal,
  openCodeModal,
  openNotifyModal,
  sortByOrders,
} = slice.actions;

// ----------------------------------------------------------------------

export function createOrder(data: {
  user_id: string;
  venue_id: string;
  total: number;
  subtotal: number;
  service_fee: number;
  cart_items: Array<{
    product_id: string;
    quantity: number;
    comments?: string;
    price: number;
    saleprice?: number;
    sale?: boolean;
  }>;
  service_tip?: number;
  discount_id?: string;
  payment_id?: string;
  payment_status?: number;
  tab_id?: number;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use RPC function to create order
      const result = await callRPC<{
        order_id: number;
        ordercode: number;
        total: number;
        orderstatus: number;
      }>('create_order', {
        p_user_id: data.user_id,
        p_venue_id: data.venue_id,
        p_total: data.total,
        p_subtotal: data.subtotal,
        p_service_fee: data.service_fee,
        p_cart_items: data.cart_items,
        p_service_tip: data.service_tip || 0,
        p_discount_id: data.discount_id || null,
        p_payment_id: data.payment_id || null,
        p_payment_status: data.payment_status || 1,
        p_tab_id: data.tab_id || null,
      });

      dispatch(slice.actions.createOrderSuccess(result));
      return result;
    } catch (error: any) {
      dispatch(slice.actions.hasError(error));
      console.error('Error creating order:', error);
      throw error;
    }
  };
}

  // ----------------------------------------------------------------------

// Helper function to get venue_user_id from current auth user
async function getCurrentVenueUserId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: venueUser } = await supabase
      .from('venue_users')
      .select('id')
      .eq('auth_user_id', user.id)
      .single();

    return venueUser?.id || null;
  } catch (error) {
    console.error('Error getting venue_user_id:', error);
    return null;
  }
}

export function getOrders(filters?: { orderstatus?: number[] }) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Get current venue_user_id
      const venueUserId = await getCurrentVenueUserId();
      if (!venueUserId) {
        dispatch(slice.actions.stopLoading());
        return;
      }

      // Get tenant venue parameters (organization_id or venue_id)
      const { organization_id, venue_id } = getCurrentTenantVenueParams();

      // Use RPC wrapper function with organization/venue filtering
      const orders = await callRPC<any[]>('get_venue_orders_filtered', {
        p_venue_user_id: venueUserId,
        p_orderstatus: filters?.orderstatus || null,
        p_organization_id: organization_id,
        p_venue_id: venue_id,
      });

      console.log('Fetched orders:', orders);

      // Transform orders to match expected format
      const transformedOrders = orders.map((order: any) => {
        const customer = order.customer;

        const venue = order.venue;

        // Transform itemsCount from JSONB array (new RPC returns itemsCount, not items_count)
        const items_count = order.itemsCount || [];

        return {
          id: order.id,
          orderstatus: order.orderstatus,
          created_at: order.created_at,
          total: parseFloat(order.total || 0),
          subtotal: parseFloat(order.subtotal || 0),
          fee: parseFloat(order.fee || 0),
          tip: parseFloat(order.tip || 0),
          ordercode: order.ordercode,
          paymentStatus: order.paymentStatus || order.paymentstatus,
          tab_id: order.tab_id,
          customer,
          venue,
          items_count,
        };
      });

      // Dispatch array directly (reducer handles both array and { orders: [...] } format)
      dispatch(slice.actions.getOrdersSuccess(orders));

    } catch (error: any) {
      dispatch(slice.actions.hasError(error));
      console.error('Error fetching orders:', error);
    }
  };
}

// ----------------------------------------------------------------------

export function getOrder(id: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use RPC function to get order details with joins
      const result = await callRPC<any>('get_venue_order_details', {
        p_order_id: parseInt(id),
      });

      if (!result || !result.order) {
        throw new Error('Order not found');
      }

      const order = result.order;
      const items = result.items || [];

      console.log('Fetched order:', result);

      // Transform to match expected format
      const transformedOrder: Order = {
        id: order.id.toString(),
        orderstatus: order.orderstatus,
        created_at: order.created_at,
        total: parseFloat(order.total || 0),
        subtotal: parseFloat(order.subtotal || 0),
        taxes: parseFloat(order.fee || 0),
        discount: parseFloat(order.tip || 0),
        discountValue: 0,
        orderNumber: order.ordercode?.toString() || '',
        paymentStatus: order.paymentstatus,
        tab_id: order.tab_id?.toString() || null,
        payment_gateway: order.payment_gateway ?? null,
        payment_format:  order.payment_format  ?? null,
        gateway_ref_id:  order.gateway_ref_id  ?? null,
        customer: order.customer ? {
          id: order.customer.id,
          firstName: order.customer.firstName,
          lastName: order.customer.lastName,
          email: order.customer.email,
          photo_url: order.customer.photo_url,
        } as any : {} as any,
        venue: order.venue ? {
          id: order.venue.id,
          name: order.venue.name,
          photo_urls: order.venue.photo_urls,
          openstatus: order.venue.openstatus,
        } as any : {} as any,
        items_count: [],
        items: items,
        logs: [],
        sent: 0,
        created_by: order.created_by || order.venue_user_createdby || '',
        updated_by: order.updated_by || order.venue_user_updatedby || '',
        updated_at: order.updated_at,
      };

      dispatch(slice.actions.getOrderSuccess(result.order));
    } catch (error: any) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function updateOrder(data: {
  id: number;
  orderstatus: number;
  paymentStatus?: number;
  venueUserId?: string; // Deprecated - will be ignored, auth.uid() used instead
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use secure RPC function (uses auth.uid() internally, no venueUserId needed)
      const updatedOrder = await callRPC<any>('update_order_status', {
        p_order_id: data.id,
        p_orderstatus: data.orderstatus,
        p_paymentstatus: data.paymentStatus || undefined,
      });

      dispatch(slice.actions.stopLoading());
      return updatedOrder;
    } catch (error: any) {
      console.error('Error updating order:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function notifyClient(data: {
  orderId: number;
  userId: string;
  title?: string;
  body?: string;
  tabId?: number | null;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const orderId = data.orderId.toString();
      const tabId = data.tabId != null ? data.tabId.toString() : null;

      // Use Edge Function to send push notification
      await invokeFunction('push-notification', {
        userId: data.userId,
        title: data.title || 'Order Update',
        body: data.body || 'Your order has been updated',
        data: {
          orderId,
          ...(tabId ? { tabId } : {}),
          type: 'order_update',
          url: tabId ? `tab_receipt/${tabId}` : `receipt/${orderId}`,
        },
      });

      dispatch(slice.actions.stopLoading());
    } catch (error: any) {
      console.error('Error notifying client:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function validateOrder(data: {
  orderId: number;
  orderCode: number;
  qr_code_token?: string;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const venueUserId = await getCurrentVenueUserId();
      if (!venueUserId) {
        throw new Error('User not authenticated or venue user not found');
      }

      // Capture authorized Stripe holds before marking delivered/paid.
      // Non-Stripe / no PI → edge returns skipped:true (HTTP 200).
      await captureOrderPayment(data.orderId);

      const result = await callRPC('validate_order', {
        p_order_id: data.orderId,
        p_order_code: data.orderCode,
        p_venue_user_id: venueUserId,
        p_qr_code_token: data.qr_code_token || null,
      });
      
      dispatch(slice.actions.stopLoading());
      return result;
    } catch (error: any) {
      console.error('validation error', error);
      const message = formatCaptureFailure(error);
      dispatch(slice.actions.hasError(message));
      throw new Error(message);
    }
  };
}

// ----------------------------------------------------------------------

export function retryCaptureOrderPayment(orderId: number) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await captureOrderPayment(orderId);
      dispatch(slice.actions.stopLoading());
      return result;
    } catch (error: any) {
      const message = formatCaptureFailure(error);
      dispatch(slice.actions.hasError(message));
      throw new Error(message);
    }
  };
}

// ----------------------------------------------------------------------
export function fulfillOrderAsVenue(orderId: number) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await captureOrderPayment(orderId);

      const result = await callRPC<{
        order_id: number;
        orderstatus: number;
        paymentstatus: number;
      }>('fulfill_order_as_venue', {
        p_order_id: orderId,
      });

      dispatch(slice.actions.stopLoading());
      return result;
    } catch (error: any) {
      console.error('fulfill_order_as_venue error', error);
      const message = formatCaptureFailure(error) || error?.error_description || 'Error fulfilling order';
      dispatch(slice.actions.hasError(message));
      throw new Error(message);
    }
  };
}

// ----------------------------------------------------------------------

export function bulkUpdateOrders(data: {
  orderIds: number[];
  orderstatus: number;
  paymentStatus?: number;
  venueUserId?: string;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Update multiple orders
      const updatePromises = data.orderIds.map((orderId) =>
        updateTable(
          'orders',
          {
            orderstatus: data.orderstatus,
            updated_at: new Date().toISOString(),
            paymentstatus: data.paymentStatus || undefined,
            venue_user_updatedby: data.venueUserId || undefined,
          },
          { id: orderId }
        )
      );

      await Promise.all(updatePromises);

      dispatch(slice.actions.stopLoading());
      // Refresh orders list
      dispatch(getOrders());
    } catch (error: any) {
      console.error('Error bulk updating orders:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function cancelOrder(data: {
  orderId: number;
  userId: string;
  total?: number;
  subtotal?: number;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use RPC function to cancel order
      const result = await callRPC<{
        order_id: number;
        orderstatus: number;
        paymentstatus: number;
      }>('cancel_order', {
        p_order_id: data.orderId,
        p_user_id: data.userId,
        p_total: data.total || null,
        p_subtotal: data.subtotal || null,
      });

      dispatch(slice.actions.stopLoading());
      // Refresh orders list
      dispatch(getOrders());
      return result;
    } catch (error: any) {
      console.error('Error canceling order:', error);
      dispatch(slice.actions.hasError(error.message));
      throw new Error(error.message);
    }
  };
}

// ----------------------------------------------------------------------

export function getOrdersStatistics(venueId?: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use RPC function to get all statistics in one call
      const statisticsData = await callRPC<{
        ordersSummary: {
          label: string;
          value: number;
          count: number;
          percent: number;
          total: number;
          average: number;
          data: number[];
        };
        publicSummary: {
          label: string;
          value: number;
          count: number;
          percent: number;
          total: number;
          average: number;
          data: number[];
        };
        orderMonthlyTotals: Array<{
          year_month: string;
          total: number;
          count: number;
          average: number;
        }>;
        publicMonthlyTotals: Array<{
          year_month: string;
          total: number;
          value: number;
          average: number;
          label: string;
        }>;
        totalsByProduct: Array<{
          ranking: number;
          label: string;
          count: number;
          value: number;
          percent: number;
        }>;
        totalsByStatus: Array<{
          label: number;
          count: number;
          total: number;
          percent: number;
        }>;
        totalsByType: Array<{
          label: string;
          count: number;
          value: number;
          percent: number;
        }>;
        totalsByVenue: Array<{
          label: string;
          count: number;
          value: number;
        }>;
        totalsByTypePerWeekday: Array<{
          label: string;
          data: any[];
        }>;
        totalsByPaymentFormat: Array<{
          label: string;
          count: number;
          value: number;
          percent: number;
        }>;
        entranceFeeSummary: {
          label: string;
          count: number;
          value: number;
          percent: number;
          total: number;
          average: number;
          data: number[];
        };
        entranceFeeMonthlyTotals: Array<{
          year_month: string;
          total: number;
          count: number;
          average: number;
        }>;
        entranceFeeByWeekday: Array<{
          label: number;
          count: number;
          value: number;
          percent: number;
        }>;
        entranceFeeByVenue: Array<{
          label: string;
          count: number;
          value: number;
        }>;
      }>('get_order_statistics', { p_venue_id: venueId ?? null });

      // Helper function to generate random array for charts (if needed)
      const getRandomArray = (length: number, min: number, max: number) => {
        return Array.from({ length }, () => Math.floor(Math.random() * (max - min + 1)) + min);
      };

      // Add random data arrays for charts if not present
      if (!statisticsData.ordersSummary.data || statisticsData.ordersSummary.data.length === 0) {
        statisticsData.ordersSummary.data = getRandomArray(6, 70, 210);
      }
      if (!statisticsData.publicSummary.data || statisticsData.publicSummary.data.length === 0) {
        statisticsData.publicSummary.data = getRandomArray(6, 20, 250);
      }

      statisticsData.entranceFeeSummary ??= {
        label: 'kpi.entranceFeeIncome',
        count: 0,
        value: 0,
        percent: 100,
        total: 0,
        average: 0,
        data: [],
      };
      statisticsData.entranceFeeMonthlyTotals ??= [];
      statisticsData.entranceFeeByWeekday ??= [];
      statisticsData.entranceFeeByVenue ??= [];

      if (!statisticsData.entranceFeeSummary.data?.length) {
        statisticsData.entranceFeeSummary.data = getRandomArray(6, 0, 0);
      }

      dispatch(slice.actions.getOrdersStatisticsSuccess(statisticsData));

    } catch (error: any) {
      dispatch(slice.actions.hasError(error));
      console.error('Error fetching statistics:', error);
    }
  };
}

export function getOperationalSummary() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const data = await callRPC<OperationalSummary>('get_operational_summary');
      dispatch(slice.actions.getOperationalSummarySuccess(data));
    } catch (error: any) {
      dispatch(slice.actions.hasError(error));
      console.error('Error fetching operational summary:', error);
    }
  };
}